<template>
  <div class="p-8 bg-white">
    <h1>Add new widget configuration</h1>

    <Form
      :form-id="formId"
      @submit="save"
    >
      <WidgetFields />

      <div class="my-8 flex gap-2">
        <VPButton
          component="router-link"
          :to="{ name: 'WidgetConfigIndex' }"
          type="button"
          variant="outline"
          label="Cancel"
          class="py-2 px-4 text-vp-error cursor-pointer"
        />
        <VPButton
          type="submit"
          label="Save"
          class="py-2 px-4"
        />
      </div>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { Form, useFormsStoreComposable } from 'vuniform';
import WidgetFields from '@/components/widget/WidgetFields.vue';
import VPButton from '@/components/form/VPButton.vue';
import { useWidgetsStore } from '@/stores/widgets';
import { useRootStore } from '@/stores/root';
import type { WidgetAttributes } from '@/generated/graphql';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { onMounted } from 'vue';
import type { WidgetConfig } from '@/types/WidgetConfig';

const { formId, formsStore, formGetValues } = useFormsStoreComposable('widget-config-form-new');

const toast = useToast();
const router = useRouter();
const route = useRoute();
const rootStore = useRootStore();
const widgetsStore = useWidgetsStore();

onMounted(async () => {
  if (!route.params.widgetId) {
    return;
  }

  await widgetsStore.fetchWidgets(rootStore.currentAccountId);
  const widget = widgetsStore.widgets.find(({ id }) => id == +route.params.widgetId);
  if (!widget) {
    return;
  }

  const newWidget: WidgetConfig = {
    ...widget,
    id: undefined,
    description: `${widget.description} (Copy)`,
  };

  formsStore.SET_INITIAL_VALUES({ formId, values: newWidget, clearDirty: false });
  formsStore.RESET_FIELD_VALUES(formId);
});

const save = async () => {
  const values = formGetValues() as WidgetAttributes;

  await widgetsStore.saveWidget(rootStore.currentAccountId, values);
  toast.success('Widget configuration has been created!');
  await router.push({ name: 'WidgetConfigIndex' });
};
</script>
